import {
  convertToSeconds,
  getHoursFromSeconds,
  getMinutesFromSeconds,
} from '@common/util/datetime';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import inputClasses from './Input.module.scss';

export default function Input(props) {
  const {
    name,
    type,
    value,
    placeholder,
    onChange,
    className,
    fullWidth,
    ...other
  } = props;
  const [currentValue, setCurrentValue] = useState(
    value || type === 'duration' ? 0 : '',
  );

  const [durationHours, setDurationHours] = useState(() => {
    if (type === 'duration' && value) {
      return getHoursFromSeconds(value);
    }
    return '0';
  });
  const [durationMinutes, setDurationMinutes] = useState(() => {
    if (type === 'duration' && value) {
      return getMinutesFromSeconds(value);
    }
    return '00';
  });

  const cs = cx(
    inputClasses.input,
    {
      [inputClasses[`full-width`]]: fullWidth,
      [inputClasses.durationInputContainer]: type === 'duration',
    },
    className,
  );

  const isValidDurationValue = (durationValue) => {
    // Ensure the value contains only digits or is empty
    return /^\d*$/.test(durationValue);
  };

  const handleHoursChange = (ev) => {
    const inputHours = ev.target.value;
    if (isValidDurationValue(inputHours)) {
      setDurationHours(inputHours);

      if (onChange) {
        const totalSeconds = convertToSeconds(inputHours, durationMinutes);
        onChange(totalSeconds, ev);
      }
    }
  };

  const handleMinutesChange = (ev) => {
    const inputMinutes = ev.target.value;
    if (
      isValidDurationValue(inputMinutes) &&
      parseInt(inputMinutes || '0', 10) < 60
    ) {
      setDurationMinutes(inputMinutes);

      if (onChange) {
        const totalSeconds = convertToSeconds(durationHours, inputMinutes);
        onChange(totalSeconds, ev);
      }
    }
  };

  const handleChange = (event) => {
    setCurrentValue(event.target.value);
    if (onChange) {
      onChange(event.target.value, event);
    }
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return type === 'duration' ? (
    <div className={cs}>
      <input
        type="text"
        className={inputClasses.durationHoursInput}
        onInput={handleHoursChange}
        value={durationHours}
        style={{
          width: durationHours.length > 1 ? `${durationHours.length}ch` : '1ch',
        }}
      />
      <span>h:</span>
      <input
        type="text"
        className={inputClasses.durationMinutesInput}
        onInput={handleMinutesChange}
        value={durationMinutes}
        style={{
          width:
            durationMinutes.length > 1 ? `${durationMinutes.length}ch` : '1ch',
        }}
      />
      <span>m</span>
    </div>
  ) : (
    <input
      className={cs}
      id={name}
      name={name}
      type={type}
      value={currentValue || ''}
      onChange={(evt) => handleChange(evt)}
      placeholder={placeholder}
      {...other}
    />
  );
}

export function InputLabel({ name, children }) {
  const cn = cx(inputClasses.label);
  return (
    <label htmlFor={name} className={cn}>
      {children}
    </label>
  );
}

export function InputError({ children }) {
  const cn = cx(inputClasses.error);

  return <span className={cn}>{children}</span>;
}
export function InputContainer({
  children,
  placeholder,
  name,
  className,
  classNameContainer,
  prefix,
  suffix,
  error,
  inputDisabled,
  containerFocus,
}) {
  const cn = cx(
    inputClasses.field,
    error && inputClasses.fieldError,
    prefix || (suffix && inputClasses.prefix),
    prefix && suffix && inputClasses.prefixAndSuffix,
    inputDisabled && inputClasses.isDisabled,
    className,
  );

  const ccn = cx(inputClasses.container, classNameContainer);

  return (
    <div className={ccn} onFocus={containerFocus}>
      <InputLabel name={name}>{placeholder}</InputLabel>
      <div className={cn}>
        {prefix}
        {children}
        {suffix}
      </div>
      {error && <InputError>{error}</InputError>}
    </div>
  );
}

InputContainer.propTypes = {
  className: PropTypes.string,
  classNameContainer: PropTypes.string,
  children: PropTypes.any.isRequired,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  error: PropTypes.string,
  inputDisabled: PropTypes.bool,
  containerFocus: PropTypes.func,
};

InputLabel.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
};

Input.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'password',
    'email',
    'number',
    'date',
    'duration',
  ]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  placeholder: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  classNameContainer: PropTypes.string,
  fullWidth: PropTypes.bool,
  props: PropTypes.any,
};
InputError.propTypes = {
  children: PropTypes.any,
};
